import { Injectable } from '@angular/core';
import { ConsentFormsService } from './consent-forms.interface';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  ConsentForm,
  ConsentFormApi,
  ConsentFormRequest,
  FormStatus,
  NppActionNeededSummary,
  NppOutcome,
  NppPendingPrompt,
  Page,
  PendingConsentSummary,
} from '@models';

@Injectable()
export class ConsentFormsMockService extends ConsentFormsService {
  private mockData: ConsentFormApi[] = [
    {
      id: '1',
      patientName: 'John Doe',
      appointmentType: 'Consultation',
      formName: 'Patient Consent',
      providerName: 'Dr. Brown',
      status: FormStatus.Sent,
      language: 'en',
      requestedAtUtc: '2026-03-12T10:00:00Z',
      completedAtUtc: null,
      createdAtUtc: '2026-03-12T10:00:00Z',
      signature: '',
      patientDateOfBirth: '',
      consentFormHtml: '',
    },
    {
      id: '2',
      patientName: 'Jane Smith',
      appointmentType: 'Follow Up',
      formName: 'Patient Consent',
      providerName: 'Dr. Wilson',
      status: FormStatus.Completed,
      requestedAtUtc: '2026-03-10T10:00:00Z',
      completedAtUtc: '2026-03-10T11:00:00Z',
      createdAtUtc: '2026-03-12T10:00:00Z',
      language: 'en',
      patientDateOfBirth: '',
      consentFormHtml: '',
    },
    {
      id: '3',
      patientName: 'Alice Johnson',
      appointmentType: 'Checkup',
      formName: 'Patient Consent',
      providerName: 'Dr. Taylor',
      status: FormStatus.Completed,
      language: 'en',
      requestedAtUtc: '2026-03-11T09:00:00Z',
      completedAtUtc: '2026-03-11T09:30:00Z',
      createdAtUtc: '2026-03-12T10:00:00Z',
      patientDateOfBirth: '',
      consentFormHtml: '',
    },
    {
      id: '4',
      patientName: 'Bob Lee',
      appointmentType: 'Consultation',
      formName: 'Patient Consent',
      providerName: 'Dr. Anderson',
      status: FormStatus.Sent,
      language: 'en',
      requestedAtUtc: '2026-03-12T11:00:00Z',
      completedAtUtc: null,
      createdAtUtc: '2026-03-12T10:00:00Z',
      patientDateOfBirth: '',
      consentFormHtml: '',
    },
  ];

  private mockDetails: Record<
    string,
    ConsentFormApi & { consentFormHtml: string }
  > = {
    '1': {
      ...this.mockData[0],
      consentFormHtml: '<h1>Consent</h1><p>John Doe agrees to treatment</p>',
    },
    '2': {
      ...this.mockData[1],
      consentFormHtml: '<h1>Consent</h1><p>Jane Smith already signed</p>',
    },
    '3': {
      ...this.mockData[2],
      consentFormHtml: '<h1>Consent</h1><p>Alice Johnson signed</p>',
    },
    '4': {
      ...this.mockData[3],
      consentFormHtml: '<h1>Consent</h1><p>Bob Lee agrees to treatment</p>',
    },
  };

  override getConsentRequests(
    params: ConsentFormRequest
  ): Observable<Page<ConsentFormApi>> {
    let data = [...this.mockData];

    if (params.statuses?.length) {
      const statuses = params.statuses;
      data = data.filter(f => statuses.includes(f.status));
    } else if (params.status) {
      data = data.filter(f => f.status === params.status);
    }

    if (params.patientName) {
      const search = params.patientName.toLowerCase();
      data = data.filter(f => f.patientName.toLowerCase().includes(search));
    }

    if (params.orderBy === 'createdAtUtc') {
      data.sort((a, b) => {
        const aTime = new Date(a.createdAtUtc ?? '').getTime();
        const bTime = new Date(b.createdAtUtc ?? '').getTime();
        return params.desc ? bTime - aTime : aTime - bTime;
      });
    }

    const start = (params.page - 1) * params.pageSize;
    const items = data.slice(start, start + params.pageSize);

    return of({
      items,
      totalItems: data.length,
      pageNumber: params.page,
      pageSize: params.pageSize,
      totalPages: Math.ceil(data.length / params.pageSize),
    }).pipe(delay(500));
  }

  override getConsentForm(id: string): Observable<ConsentFormApi> {
    const item = this.mockDetails[id];
    if (!item) {
      return throwError(() => new Error('Consent form not found')).pipe(
        delay(300)
      );
    }
    return of(item).pipe(delay(400));
  }

  override assignToPatient(body: {
    patientId: string;
    templateId: string;
  }): Observable<ConsentFormApi> {
    const newId = (this.mockData.length + 1).toString();
    const newForm: ConsentFormApi & { consentFormHtml: string } = {
      id: newId,
      patientName: `Patient ${body.patientId}`,
      appointmentType: 'New',
      formName: 'New Consent Form',
      providerName: 'Dr. Mock',
      status: FormStatus.Sent,
      language: 'en',
      requestedAtUtc: new Date().toISOString(),
      completedAtUtc: null,
      createdAtUtc: new Date().toISOString(),
      signature: 'Patient',
      patientDateOfBirth: '2026-03-12T10:00:00Z',
      consentFormHtml: `<h1>Consent Form</h1><p>Patient ${body.patientId} assigned</p>`,
    };

    this.mockData.push(newForm);
    this.mockDetails[newId] = newForm;

    return of(newForm).pipe(delay(400));
  }

  override sign(
    submissionId: string,
    body: {
      patientName: string;
      dateOfBirth: string;
      signature: string;
      guardianName?: string | null;
      guardianRelationship?: string | null;
    }
  ): Observable<string> {
    const item = this.mockData.find(f => f.id === submissionId);
    const detail = this.mockDetails[submissionId];

    if (!item || !detail) {
      return throwError(() => new Error('Submission not found')).pipe(
        delay(300)
      );
    }

    if (item.status === FormStatus.Completed) {
      return throwError(() => new Error('Already signed')).pipe(delay(300));
    }

    const now = new Date().toISOString();
    item.status = FormStatus.Completed;
    item.completedAtUtc = now;

    detail.status = FormStatus.Completed;
    detail.completedAtUtc = now;
    item.signature = body.signature;

    return of('Signed').pipe(delay(500));
  }

  override recordNppOutcome(
    submissionId: string,
    body: {
      outcome: NppOutcome;
      patientName: string;
      dateOfBirth?: string | null;
    }
  ): Observable<NppOutcome> {
    const item = this.mockData.find(f => f.id === submissionId);
    const detail = this.mockDetails[submissionId];
    const now = new Date().toISOString();

    if (item) {
      item.status = FormStatus.Completed;
      item.completedAtUtc = now;
    }
    if (detail) {
      detail.status = FormStatus.Completed;
      detail.completedAtUtc = now;
    }

    return of(body.outcome).pipe(delay(500));
  }

  override deleteSubmission(submissionId: string): Observable<unknown> {
    this.mockData = this.mockData.filter(f => f.id !== submissionId);

    delete this.mockDetails[submissionId];

    return of(undefined).pipe(delay(300));
  }

  override getSubmissionsByBookingIntentId(
    _id: string
  ): Observable<ConsentForm[]> {
    return of([this.mockData[0], this.mockData[1]]);
  }

  override getPdf(
    _submissionId: string,
    _action: 'Download' | 'View'
  ): Observable<Blob> {
    const blob = new Blob([], { type: 'application/pdf' });
    return of(blob);
  }

  markAsOpened(submissionId: string): Observable<void> {
    this.mockData = this.mockData.map(form => {
      if (form.id !== submissionId) return form;
      if (form.status !== FormStatus.Sent) return form;
      return { ...form, status: FormStatus.Opened };
    });

    return of();
  }

  getSubmissionByAppointmentId(
    _appointmentId: string
  ): Observable<ConsentForm[]> {
    return of([this.mockData[0]]);
  }

  override getPendingSummary(
    _patientId: string
  ): Observable<PendingConsentSummary> {
    const unsigned = this.mockData.filter(
      f => f.status !== FormStatus.Completed
    );
    const target = unsigned[0] ?? null;

    return of<PendingConsentSummary>({
      hasUnsigned: target !== null,
      unsignedCount: unsigned.length,
      reminderTargetSubmissionId: target?.id ?? null,
      reminderTargetFormName: target?.formName ?? null,
    }).pipe(delay(300));
  }

  override sendReminder(_submissionId: string): Observable<void> {
    return of(undefined).pipe(delay(300));
  }

  override getNppActionNeeded(
    _patientId: string
  ): Observable<NppActionNeededSummary> {
    return of({ hasActionNeeded: false }).pipe(delay(300));
  }

  override getMyPendingNpp(): Observable<NppPendingPrompt> {
    return of<NppPendingPrompt>({ hasPending: false }).pipe(delay(300));
  }

  override acknowledgeNppFromPortal(
    submissionId: string,
    _body: {
      patientName: string;
      dateOfBirth?: string | null;
    }
  ): Observable<NppOutcome> {
    const item = this.mockData.find(f => f.id === submissionId);
    if (item) {
      item.status = FormStatus.Completed;
      item.completedAtUtc = new Date().toISOString();
    }

    return of(NppOutcome.Acknowledged).pipe(delay(500));
  }
}
